import { ReactElement, ReactNode } from 'react';
import { HiOutlineChevronRight } from 'react-icons/hi2';

interface CollapsiblePanelProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
  subtitle?: string;
  /** Max visible table rows before scrolling (default 50). Each row ≈ 37px + 41px header. */
  maxRows?: number;
  /** When true, content grows to fit with no scroll constraint. */
  noScroll?: boolean;
}

// Row height ≈ 37px, table header ≈ 41px, plus 16px padding
const ROW_HEIGHT = 37;
const TABLE_HEADER_HEIGHT = 41;
const PANEL_PADDING = 16;

export default function CollapsiblePanel({
  title,
  isOpen,
  onToggle,
  children,
  subtitle,
  maxRows = 50,
  noScroll = false,
}: CollapsiblePanelProps): ReactElement {
  const maxHeight = noScroll
    ? undefined
    : TABLE_HEADER_HEIGHT + ROW_HEIGHT * maxRows + PANEL_PADDING;

  return (
    <div className="cr-mb-4 cr-rounded-lg cr-border-2 cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25">
      <button
        onClick={onToggle}
        className="cr-flex cr-w-full cr-items-center cr-rounded-t-lg cr-bg-cirrusBlue-50 cr-px-4 cr-py-3 cr-text-left cr-transition-colors hover:cr-bg-cirrusBlue-100"
      >
        <HiOutlineChevronRight
          className={`cr-size-5 cr-stroke-[3px] cr-text-cirrusBlue-400 cr-transition-transform cr-duration-[300ms] ${isOpen ? 'cr-rotate-90' : ''}`}
        />
        <span className="cr-ml-2 cr-text-sm cr-font-semibold cr-uppercase cr-text-cirrusBlue-400">{title}</span>
        {subtitle && (
          <span className="cr-ml-3 cr-text-xs cr-text-cirrusBlue-300">{subtitle}</span>
        )}
      </button>
      {isOpen && (
        <div
          className={`cr-p-2 ${noScroll ? '' : 'cr-overflow-y-auto'}`}
          style={maxHeight ? { maxHeight } : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}
